import { applyImport, parseExport } from '../../data/export';
import { de } from '../../i18n/de';
import { BackupError, openBackup, parseEnvelope } from './envelope';
import { completeCredentials } from './credentials';
import { prerenderMissing } from '../speech/prerender';
import { useLearning } from '../learning/LearningService';
import { useProfileStore } from '../profile/ProfileStore';
import { useUiStore } from '../../app/uiStore';
import { speechService } from '../speech/SpeechService';

/**
 * Restoring, from wherever the bytes came from.
 *
 * **One code path with one branch at the front.** 4.7 asks for "restore from Azure or from a local file, same
 * code path", and the way to get that is to decide by looking at the file rather than by remembering where it
 * came from. Both are JSON with a `format` field, so:
 *
 *   `sags-export` → a plaintext export. Apply it.
 *   `sags-backup` → a sealed envelope. Open it with the six words, then apply the same way.
 *
 * The useful consequence is one nobody asked for: a caregiver who kept an *encrypted* copy in Files can
 * restore from it, and somebody handed a decrypted export can restore from that. The route stopped mattering.
 */

export type RestoreKind = 'plaintext' | 'sealed' | 'unknown';

/**
 * What kind of file this is, without decrypting anything.
 *
 * Deliberately tolerant of the wrong file entirely: the most likely mistake is picking some other JSON out of
 * Files, and that has to produce "this is not a backup" rather than "wrong passphrase".
 */
export function classify(text: string): RestoreKind {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return 'unknown';
  }
  if (!parsed || typeof parsed !== 'object') return 'unknown';

  const format = (parsed as { format?: unknown }).format;
  if (format === 'sags-export') return 'plaintext';
  if (format === 'sags-backup') return 'sealed';
  return 'unknown';
}

export interface RestoreInput {
  text: string;
  /** Required for a sealed file, ignored for a plaintext one. */
  passphrase?: string;
  /** Injected in tests; production re-renders through the live speech chain. */
  rerenderAudio?: (voiceId: string) => Promise<void>;
}

export interface RestoreResult {
  kind: RestoreKind;
  /** Phrases, quick fires and abbreviations restored — what a caregiver counts to believe it worked. */
  phrases: number;
  /** Set when audio re-rendering was started. It runs in the background and is not waited for. */
  rerenderStarted: boolean;
}

/**
 * Apply a backup.
 *
 * Throws `BackupError` (German, for a caregiver) for anything a human can act on: wrong file, wrong
 * passphrase, a backup from a newer app.
 */
export async function restoreFrom(input: RestoreInput): Promise<RestoreResult> {
  const kind = classify(input.text);
  if (kind === 'unknown') throw new BackupError(de.backupInvalid);

  let plaintext = input.text;
  let saltUsed: string | null = null;

  if (kind === 'sealed') {
    if (!input.passphrase?.trim()) throw new BackupError(de.backupPassphraseRequired);
    // Throws a German BackupError for a wrong passphrase or a newer envelope, each with its own message.
    plaintext = await openBackup(input.text, input.passphrase);
    // The header is plaintext by design, so the salt is readable without the passphrase. Kept because it is
    // half of what a replacement device needs to carry on backing itself up.
    saltUsed = parseEnvelope(input.text).kdf.salt;
  }

  // `parseExport` refuses a newer schema rather than silently dropping fields it does not understand, and
  // `applyImport` migrates forward and preserves the device's own credentials (C52).
  const document_ = parseExport(plaintext);
  await applyImport(document_);

  // **The lineage continues through a restore.** A replacement device typed its key off the card, and now that
  // the six words have been proven against a real envelope, it has everything needed to seal the next backup:
  // same passphrase, same salt, same cloud history. Without this the restored device would come back with the
  // person's whole vocabulary and quietly stop protecting it — and because failure is silent by design, the
  // first sign would be a warning a week later (C57).
  if (saltUsed !== null && input.passphrase) {
    await completeCredentials({ salt: saltUsed, passphrase: input.passphrase });
  }

  // **Everything holding a copy of the database in memory now holds the wrong one.**
  //
  // This was true of the Phase 1 file import too and nobody had noticed, because nobody had looked at a
  // restored device without relaunching it first. The rehearsal looked: the restored board showed *no* learned
  // words and predicted without the person's vocabulary, because `useLearning` still held the empty maps it
  // hydrated at boot. Settings were equally stale, so a restored theme or key size would not have applied
  // either.
  //
  // Re-hydrating rather than reloading the page: non-negotiable §3.2 says the app never reloads itself, and a
  // restore is not the exception it might look like — the rule is worth more than the convenience.
  await Promise.all([useProfileStore.getState().hydrate(), useLearning.getState().hydrate()]);
  // Components that read the database directly — the quick fires in the persistent bar — watch this.
  useUiStore.getState().dataReplaced();

  // Audio is excluded from every backup on purpose — regenerable, and it would bloat the file — so a restored
  // device is mute for novel utterances until it has been online once. Starting the re-render here, while a
  // caregiver is present and probably online, is the difference between that being a footnote and being
  // discovered in a conversation.
  const rerenderStarted = await startRerender(document_.settings.voiceId, input.rerenderAudio);

  return { kind, phrases: document_.phrases.length, rerenderStarted };
}

async function startRerender(
  voiceId: string,
  injected?: (voiceId: string) => Promise<void>,
): Promise<boolean> {
  if (injected) {
    // Not awaited in production either: see below.
    void injected(voiceId);
    return true;
  }

  const provider = speechService.chain().find((entry) => entry.id === 'azureTts');
  if (!provider) return false;

  // Fire and forget. It is ~50 sequential requests against a rate-limited endpoint, and a caregiver should
  // be looking at a restored board rather than a progress bar. If it fails, care mode's "Audio fehlt" count
  // says so, which is the same signal a fresh install already produces.
  void prerenderMissing({ provider, voiceId });
  return true;
}
